import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Layers, Hammer, ShoppingBag, Car, Plane, MoreHorizontal, Star } from "lucide-react";
import { Header } from "@/components/laneguide/Header";
import { Footer, StickyMobileBar } from "@/components/laneguide/Footer";
import { BankMarquee } from "@/components/laneguide/BankMarquee";
import { FeaturedBank } from "@/components/laneguide/FeaturedBank";
import { LENDERS } from "@/lib/lenders";
import avatar1 from "@/assets/avatars/avatar1.jpg";
import avatar2 from "@/assets/avatars/avatar2.jpg";
import avatar3 from "@/assets/avatars/avatar3.jpg";
import avatar4 from "@/assets/avatars/avatar4.jpg";
import { AmountInput } from "@/components/laneguide/AmountInput";
import { FaqSection } from "@/components/laneguide/PageFaq";
import { useCurrentMonth } from "@/lib/useCurrentMonth";
import { EpiProvider } from "@/lib/epi";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Svensk Låneguide – Jämför lån från 40+ banker på 60 sek" },
      { name: "description", content: "Jämför privatlån, billån, samlingslån och snabblån från Sveriges 40+ ledande banker. Endast 1 UC, gratis och utan förpliktelser." },
      { property: "og:title", content: "Svensk Låneguide – Jämför lån från 40+ banker" },
      { property: "og:description", content: "Jämför lån från Sveriges ledande banker. Endast 1 UC, gratis och utan förpliktelser." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: HomePage,
});

const purposes = [
  { id: "samla", label: "Samla lån", icon: Layers },
  { id: "renovering", label: "Renovering", icon: Hammer },
  { id: "shopping", label: "Shopping", icon: ShoppingBag },
  { id: "fordon", label: "Fordon", icon: Car },
  { id: "resa", label: "Resa", icon: Plane },
  { id: "ovrigt", label: "Övrigt", icon: MoreHorizontal },
];

function formatKr(n: number) { return new Intl.NumberFormat("sv-SE").format(n) + " kr"; }

function HomePage() {
  const { month } = useCurrentMonth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(50000);
  const [purpose, setPurpose] = useState("samla");
  const featured = LENDERS.lendo;
  const pct = (Math.max(0, amount - 1000) / (800000 - 1000)) * 100;

  function goToResults() {
    try {
      sessionStorage.setItem("laneguide-form", JSON.stringify({ amount, purpose, remarks: "nej" }));
    } catch {}
    navigate({ to: "/resultat", search: { a: amount, p: purpose, r: "nej" } as any });
  }

  return (
    <EpiProvider value="hem">
    <div className="min-h-screen">
      <Header />
      <main>
        <section className="relative overflow-hidden pb-14 pt-10 sm:pb-20 sm:pt-14">
          <div className="relative mx-auto grid max-w-7xl items-start gap-10 px-4 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:px-8">
            <div>
              <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-[3.2rem]">
                Låna pengar med <span className="underline-brand whitespace-nowrap">snabba</span> utbetalningar.
              </h1>
              <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-foreground/70 sm:text-base">
                En ansökan – erbjudanden från Sveriges ledande banker. Endast 1 UC, svar direkt och räntor från 4,5 %.
              </p>

              <div className="mt-6 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[
                    { src: avatar1, name: "Anna K." },
                    { src: avatar2, name: "Marcus S." },
                    { src: avatar3, name: "Johanna L." },
                    { src: avatar4, name: "Erik N." },
                  ].map((a) => (
                    <img
                      key={a.name}
                      src={a.src}
                      alt={`Omdöme från ${a.name}`}
                      width={36}
                      height={36}
                      loading="lazy"
                      className="h-9 w-9 rounded-full border-2 border-background object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[color:var(--gold)]">
                    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" strokeWidth={0} />)}
                    <span className="ml-1 text-sm font-bold text-foreground">4,8/5</span>
                  </div>
                  <p className="text-xs text-foreground/60">Baserat på 1 282 omdömen</p>
                </div>
              </div>

              <div className="mt-8 rounded-lg border border-border bg-card p-5 shadow-elevated sm:p-6">
                <div className="text-xs font-bold uppercase tracking-wider text-primary">Vad vill du ha för lån?</div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <label className="text-sm font-semibold text-foreground">Hur mycket vill du låna?</label>
                  <div className="flex items-center gap-1.5">
                    <AmountInput
                      value={amount} onChange={setAmount} max={800000} min={0}
                      className="w-32 rounded-md border border-input bg-card px-2 py-1 text-right font-display text-base font-extrabold text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                    <span className="text-sm font-semibold text-foreground/70">kr</span>
                  </div>
                </div>
                <input
                  type="range" min={0} max={800000} step={1000} value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  style={{ ["--val" as any]: `${pct}%` }}
                  className="range-slider mt-3 w-full"
                />
                <div className="mt-1 flex justify-between text-[11px] font-medium text-muted-foreground">
                  <span>{formatKr(0)}</span><span>{formatKr(800000)}</span>
                </div>

                <div className="mt-5 text-sm font-semibold text-foreground">Vad behöver du lånet till?</div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {purposes.map((p) => {
                    const Icon = p.icon; const active = purpose === p.id;
                    return (
                      <button key={p.id} type="button" onClick={() => setPurpose(p.id)}
                        className={`flex flex-col items-center gap-1 rounded-md border p-2 text-center transition-all ${
                          active ? "border-primary bg-primary-soft text-primary" : "border-border bg-card hover:border-primary/40"
                        }`}>
                        <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                        <span className={`text-[11px] font-semibold ${active ? "text-primary" : "text-foreground"}`}>{p.label}</span>
                      </button>
                    );
                  })}
                </div>

                <button onClick={goToResults}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-cta py-3.5 text-base font-bold text-cta-foreground shadow-md transition-all hover:brightness-105">
                  Se mina låneförslag <ArrowRight className="h-5 w-5" />
                </button>
                <p className="mt-2 text-center text-[11px] text-muted-foreground">Kostnadsfritt · Flera långivare tar ingen UC</p>
              </div>
            </div>

            <div className="relative">
              <FeaturedBank lender={featured} title={`Veckans privatlån för snabb utbetalning i ${month.toLowerCase()}`} highlightWord={month.toLowerCase()} compact />
            </div>
          </div>
        </section>

        <BankMarquee />

        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Så <span className="underline-brand">hjälper</span> vi dig
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-[15px] leading-relaxed text-foreground/70">
            Oavsett om du vill låna pengar till en oförutsedd utgift, samla dyra krediter eller finansiera ett större köp
            är vägen till pengarna densamma. Så här går processen till – från jämförelse till utbetalning.
          </p>

          <ol className="mt-10 space-y-4">
            {[
              { t: "Välj ett lån utifrån dina förutsättningar", b: "Välj en långivare i listan ovan som uppfyller dina önskemål och kriterier." },
              { t: "Gå vidare till långivarens hemsida", b: "Klicka på 'Ansök nu' för att komma till långivarens hemsida. Här anger du önskat lånebelopp och löptid." },
              { t: "Fyll i din information", b: "Fyll i det som banken efterfrågar – normalt personnummer, mobilnummer och e-postadress." },
              { t: "Signera din ansökan", b: "Skicka in din ansökan för att signera." },
              { t: "Invänta ditt besked", b: "Långivaren tar en kreditupplysning och lämnar sedan besked om ditt lån beviljas. Flera långivare tar inte en kreditupplysning via UC." },
              { t: "Utbetalning", b: "Om lånet beviljas får du pengarna på ditt konto – vissa långivare betalar ut inom 15 minuter." },
            ].map((s2, i) => (
              <li key={i} className="flex gap-4 rounded-lg border border-border bg-card p-5 shadow-card">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary text-sm font-black text-primary-foreground">
                  {i + 1}
                </div>
                <div>
                  <div className="font-display text-base font-extrabold text-foreground">{s2.t}</div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/75">{s2.b}</p>
                </div>
              </li>
            ))}
          </ol>

        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Välj din <span className="underline-brand">lånetyp</span>
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { to: "/privatlan", t: "Privatlån", d: "Upp till 800 000 kr utan säkerhet." },
              { to: "/billan", t: "Billån", d: "Finansiera bilen med lägst ränta." },
              { to: "/samla-lan", t: "Samla lån", d: "Sänk månadskostnaden upp till 45 %." },
              { to: "/snabblan", t: "Snabblån", d: "Utbetalning samma dag, upp till 200k." },
              { to: "/smslan", t: "SMS-lån", d: "Mindre snabblån direkt via mobilen." },
              { to: "/lan-utan-uc", t: "Lån utan UC", d: "Utan att påverka din UC-poäng." },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="group rounded-lg border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevated">
                <div className="font-display text-xl font-extrabold text-foreground">{c.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary">
                  Jämför nu <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </section>
        <FaqSection />
      </main>
      <Footer />
      <StickyMobileBar lender={featured} product="privatlån" />
    </div>
    </EpiProvider>
  );
}
